import React, { useEffect, useState } from "react";
import "./cancellationRefund.css";
import usePageMeta from "../../../hooks/usePageMeta";

export default function RefundPolicy() {
  usePageMeta(
  "Cancellation & Refund Policy | MakeBeeTrip",
  "MakeBeeTrip's cancellation and refund policy for flight, hotel, bus and holiday bookings, including applicable charges and refund timelines.",
  "makebeetrip cancellation policy, refund policy, cancellation charges, booking cancellation, refund status, travel refund"
);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const policySections = [
    {
      title: "General Cancellation Policy",
      content: (
        <>
          <p>
            Cancellation of a booking is subject to the cancellation terms and
            conditions applicable to the particular travel service.
          </p>

          <p>
            Different suppliers may have different cancellation rules, charges
            and refund conditions.
          </p>

          <p>
            Before confirming a booking, customers are advised to carefully
            review the applicable:
          </p>

          <ul>
            <li>Cancellation policy</li>
            <li>Refund policy</li>
            <li>Fare rules</li>
            <li>Amendment charges</li>
            <li>No-show conditions</li>
            <li>Supplier terms and conditions</li>
          </ul>

          <p>
            The specific cancellation conditions communicated at the time of
            booking will apply to the respective booking.
          </p>
        </>
      ),
    },

    {
      title: "Flight Cancellation",
      content: (
        <>
          <p>
            Flight cancellations are subject to the applicable airline's fare
            rules and cancellation policy.
          </p>

          <p>
            Depending on the airline and fare type, cancellation may result in:
          </p>

          <ul>
            <li>Full or partial refund</li>
            <li>Cancellation charges</li>
            <li>Airline penalties</li>
            <li>MakeBeeTrip service charges</li>
            <li>Non-refundable fare</li>
            <li>Applicable taxes or other deductions</li>
          </ul>

          <p>
            Some promotional, discounted or special fares may be completely
            non-refundable.
          </p>

          <p>
            If a customer requests cancellation, the applicable refund will be
            calculated according to the airline's rules and the applicable
            MakeBeeTrip service charges.
          </p>
        </>
      ),
    },

    {
      title: "Hotel Cancellation",
      content: (
        <>
          <p>
            Hotel cancellation is subject to the cancellation policy of the
            hotel and/or hotel supplier.
          </p>

          <p>Depending on the selected rate, cancellation may be:</p>

          <ul>
            <li>Free before a specified deadline</li>
            <li>Subject to a cancellation charge</li>
            <li>Partially refundable</li>
            <li>Completely non-refundable</li>
          </ul>

          <p>
            Non-refundable hotel bookings generally cannot be refunded after
            confirmation unless specifically permitted by the hotel or
            supplier.
          </p>
        </>
      ),
    },

    {
      title: "Holiday Package Cancellation",
      content: (
        <>
          <p>
            Holiday package cancellations are subject to the cancellation
            conditions mentioned in the applicable quotation, itinerary or
            booking confirmation.
          </p>

          <p>
            A package may include multiple services such as:
          </p>

          <ul>
            <li>Hotels</li>
            <li>Flights</li>
            <li>Transportation</li>
            <li>Sightseeing</li>
            <li>Activities</li>
            <li>Transfers</li>
            <li>Visa services</li>
            <li>Other travel arrangements</li>
          </ul>

          <p>
            Cancellation charges may therefore depend on the cancellation
            policies of the individual suppliers involved.
          </p>

          <p>
            Any applicable supplier penalties, non-refundable amounts, service
            charges or other costs may be deducted from the refundable amount.
          </p>
        </>
      ),
    },

    {
      title: "Cab & Transfer Cancellation",
      content: (
        <>
          <p>
            Cancellation of cab or transfer services is subject to the terms of
            the applicable transportation provider.
          </p>

          <p>Cancellation charges may depend on:</p>

          <ul>
            <li>Time of cancellation</li>
            <li>Pickup date and time</li>
            <li>Vehicle type</li>
            <li>Service provider</li>
            <li>Booking conditions</li>
          </ul>

          <p>
            Late cancellation or no-show may result in partial or complete loss
            of the amount paid.
          </p>
        </>
      ),
    },

    {
      title: "Bus Booking Cancellation",
      content: (
        <>
          <p>
            Bus bookings are subject to the cancellation and refund rules of
            the applicable bus operator.
          </p>

          <p>
            Cancellation charges and refund amounts may vary depending on the
            operator, route, ticket type and time remaining before departure.
          </p>
        </>
      ),
    },

    {
      title: "Visa Service Cancellation",
      content: (
        <>
          <p>
            Visa service fees and government/embassy/consulate fees may be
            non-refundable once processing has started or documents have been
            submitted.
          </p>

          <p>
            Visa approval is determined by the relevant embassy, consulate,
            immigration authority or visa-issuing authority.
          </p>

          <p>
            Visa rejection does not automatically entitle the customer to a
            refund of all amounts paid.
          </p>

          <p>
            Any refundable amount will depend on the applicable visa service
            provider's terms and the stage of processing.
          </p>
        </>
      ),
    },

    {
      title: "Service Charges",
      content: (
        <>
          <p>
            MakeBeeTrip may apply applicable service charges for booking,
            cancellation, amendment, processing or other travel-related
            services.
          </p>

          <p>
            Such charges may be deducted from the refund amount where
            applicable.
          </p>

          <p>
            Any applicable service charges will be communicated according to
            the booking or service conditions.
          </p>
        </>
      ),
    },

    {
      title: "Non-Refundable Bookings",
      content: (
        <>
          <p>
            Certain travel products may be marked as{" "}
            <strong>Non-Refundable</strong>.
          </p>

          <p>Examples may include:</p>

          <ul>
            <li>Special airline fares</li>
            <li>Promotional fares</li>
            <li>Non-refundable hotel rates</li>
            <li>Certain holiday package components</li>
            <li>Visa processing fees</li>
            <li>Certain transportation bookings</li>
            <li>Supplier-specific special offers</li>
          </ul>

          <p>
            For non-refundable bookings, the amount paid may not be refundable
            except where specifically permitted by the applicable supplier or
            law.
          </p>
        </>
      ),
    },

    {
      title: "No-Show",
      content: (
        <>
          <p>
            If a customer does not use the booked service without cancelling it
            according to the applicable cancellation procedure, it may be
            treated as a <strong>No-Show</strong>.
          </p>

          <p>
            No-show bookings may be non-refundable or may attract additional
            charges according to the applicable supplier's rules.
          </p>

          <p>Examples include:</p>

          <ul>
            <li>Missing a flight</li>
            <li>Failing to check in at a hotel</li>
            <li>Missing a bus</li>
            <li>Not appearing for a scheduled cab or transfer</li>
            <li>Not using a confirmed travel service</li>
          </ul>
        </>
      ),
    },

    {
      title: "Airline or Supplier Cancellation",
      content: (
        <>
          <p>
            If an airline, hotel, transportation provider or other supplier
            cancels or materially changes a service, MakeBeeTrip will assist
            the customer with the available options provided by the relevant
            supplier.
          </p>

          <p>
            Depending on the supplier's policy, available options may include:
          </p>

          <ul>
            <li>Refund</li>
            <li>Rescheduling</li>
            <li>Alternative service</li>
            <li>Credit or voucher</li>
            <li>Rebooking</li>
          </ul>

          <p>
            The available option will depend on the applicable supplier's
            rules.
          </p>
        </>
      ),
    },

    {
      title: "Refund Processing",
      content: (
        <>
          <p>
            Once a refund is approved, MakeBeeTrip will initiate or process the
            applicable refund according to the relevant booking and supplier
            conditions.
          </p>

          <p>
            Refunds may take time to reflect in the customer's original
            payment method depending on:
          </p>

          <ul>
            <li>Payment gateway</li>
            <li>Bank</li>
            <li>Card issuer</li>
            <li>UPI provider</li>
            <li>Travel supplier</li>
            <li>Refund processing timelines</li>
          </ul>

          <p>
            MakeBeeTrip cannot guarantee a specific bank crediting time after
            the refund has been processed.
          </p>
        </>
      ),
    },

    {
      title: "Refund to Original Payment Method",
      content: (
        <>
          <p>
            Where possible, approved refunds will generally be processed to the
            original payment method used for the booking.
          </p>

          <p>
            MakeBeeTrip may request additional information where necessary to
            verify the transaction or process the refund.
          </p>
        </>
      ),
    },

    {
      title: "Refund Deductions",
      content: (
        <>
          <p>
            Where applicable, the refundable amount may be calculated after
            deducting:
          </p>

          <ul>
            <li>Airline cancellation charges</li>
            <li>Hotel cancellation charges</li>
            <li>Supplier penalties</li>
            <li>Transportation cancellation charges</li>
            <li>Visa processing charges</li>
            <li>Government or statutory charges</li>
            <li>MakeBeeTrip service charges</li>
            <li>Payment gateway charges, where applicable</li>
            <li>Other non-refundable amounts</li>
          </ul>

          <p>
            The final refund amount will depend on the applicable booking
            conditions.
          </p>
        </>
      ),
    },

    {
      title: "How to Request a Cancellation",
      content: (
        <>
          <p>
            Customers wishing to cancel a booking should contact MakeBeeTrip as
            soon as possible.
          </p>

          <p>Please provide:</p>

          <ul>
            <li>Booking reference</li>
            <li>Passenger/customer name</li>
            <li>Travel date</li>
            <li>Service booked</li>
            <li>Reason for cancellation, where required</li>
          </ul>

          <p>
            Cancellation requests received through unofficial channels or after
            the applicable cancellation deadline may not be accepted.
          </p>
        </>
      ),
    },

    {
      title: "Amendment or Rescheduling",
      content: (
        <>
          <p>
            Requests to change a booking are subject to availability and the
            applicable supplier's rules.
          </p>

          <p>Additional charges may apply for:</p>

          <ul>
            <li>Date changes</li>
            <li>Flight changes</li>
            <li>Hotel changes</li>
            <li>Passenger amendments</li>
            <li>Route changes</li>
            <li>Vehicle changes</li>
            <li>Package modifications</li>
          </ul>

          <p>
            Some bookings may not permit amendments or rescheduling.
          </p>
        </>
      ),
    },

    {
      title: "Processing Time",
      content: (
        <>
          <p>
            The time required to process a cancellation or refund may vary
            depending on the travel supplier and payment method.
          </p>

          <p>
            Once the applicable supplier confirms the refund amount, MakeBeeTrip
            will process the refund as per the applicable procedure.
          </p>

          <p>
            The time taken by the bank or payment gateway to credit the amount
            may be outside MakeBeeTrip's control.
          </p>
        </>
      ),
    },

    {
      title: "Important Notice",
      important: true,
      content: (
        <>
          <p>
            Every travel booking is governed by the specific terms and
            conditions applicable to that booking.
          </p>

          <p>
            The cancellation and refund conditions displayed or communicated at
            the time of booking shall take precedence where they differ from
            this general policy.
          </p>

          <p>
            Customers are advised to carefully review the booking conditions
            before making payment.
          </p>
        </>
      ),
    },

    {
      title: "Contact Us",
      contact: true,
      content: (
        <div className="refund-contact">
          <p>
            For cancellation, refund or amendment assistance, please contact:
          </p>

          <div className="refund-contact-card">
            <p>
              <strong>MakeBeeTrip Private Limited</strong>
            </p>

            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:9355471999">9355471999</a>
            </p>

            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:Holidays@makebeetrip.com">
                Holidays@makebeetrip.com
              </a>
            </p>

            <p>
              <strong>Website:</strong>{" "}
              <a
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noreferrer"
              >
                https://makebeetrip.com/
              </a>
            </p>

            <div className="refund-address">
              <strong>Registered/Mailing Address:</strong>
              <span>
                WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
                Delhi, India
              </span>
            </div>

            <p>
              <strong>CIN:</strong> U79110DC2026PTC471178
            </p>
          </div>

          <div className="refund-tagline">
            <strong>MakeBeeTrip</strong>
            <span>Your Journey, Our Commitment.</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="refund-page">
      {/* Header */}
      <header className="refund-header">
        <div className="refund-header-inner">
          <button
            onClick={() => window.history.back()}
            className="refund-back-button"
            aria-label="Go back"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>

          <div className="refund-brand-pill">
            <span>MakeBeeTrip</span>

            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="refund-hero">
        <div className="refund-hero-glow" />

        <div className="refund-hero-content">
          <span className="refund-hero-label">
            MakeBeeTrip Private Limited
          </span>

          <h1>Cancellation &amp; Refund Policy</h1>

          <p className="refund-updated">
            Last Updated: 24 September 2026
          </p>

          <p className="refund-hero-description">
            Our Cancellation &amp; Refund Policy explains the general
            conditions applicable to cancellations, refunds and amendments for
            bookings made through MakeBeeTrip.
          </p>
        </div>
      </section>

      {/* Intro */}
      <main className="refund-main">
        <section className="refund-intro">
          <p>
            At <strong>MakeBeeTrip Private Limited</strong>, we understand that
            travel plans may change. Our Cancellation &amp; Refund Policy
            explains the general conditions applicable to cancellations,
            refunds and amendments for bookings made through{" "}
            <a
              href="https://makebeetrip.com/"
              target="_blank"
              rel="noreferrer"
            >
              https://makebeetrip.com/
            </a>
            .
          </p>

          <p>
            Because MakeBeeTrip provides multiple travel services through
            different airlines, hotels, transportation providers, tour
            operators and other travel suppliers, the applicable cancellation
            and refund conditions may vary depending on the service booked.
          </p>
        </section>

        {/* Desktop */}
        <div className="refund-desktop-sections">
          {policySections.map((section, index) => (
            <article
              key={index}
              className={`refund-section-card ${
                section.important ? "refund-important-card" : ""
              } ${section.contact ? "refund-contact-section" : ""}`}
            >
              <div className="refund-section-heading">
                <span className="refund-section-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <h2>{section.title}</h2>
              </div>

              <div className="refund-section-content">
                {section.content}
              </div>
            </article>
          ))}
        </div>

        {/* Mobile */}
        <div className="refund-mobile-sections">
          {policySections.map((section, index) => {
            const isOpen = openAccordion === index;

            return (
              <article
                key={index}
                className={`refund-mobile-card ${
                  section.important ? "refund-important-card" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  className="refund-mobile-trigger"
                  aria-expanded={isOpen}
                >
                  <span className="refund-mobile-title">
                    <span className="refund-section-number">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <span>{section.title}</span>
                  </span>

                  <svg
                    className={`refund-chevron ${
                      isOpen ? "refund-chevron-open" : ""
                    }`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                <div
                  className={`refund-mobile-content ${
                    isOpen ? "refund-mobile-content-open" : ""
                  }`}
                >
                  <div>{section.content}</div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Supplier disclaimer supplied with the company policy */}
        <div className="refund-final-disclaimer">
          <p>
            All cancellations and refunds are subject to the applicable
            airline, hotel, transportation provider, visa service provider,
            tour operator or other travel supplier's terms and conditions.
          </p>
        </div>
      </main>

      {/* Back to Top */}
      <button
        type="button"
        onClick={scrollToTop}
        className={`refund-back-to-top ${
          showScrollTop ? "refund-back-to-top-visible" : ""
        }`}
        aria-label="Back to Top"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 10l7-7m0 0l7 7m-7-7v18"
          />
        </svg>
      </button>
    </div>
  );
}